import type { ProductOrder } from '@/contexts/products/ProductsContext'
import type { CountryItem } from '@/types/countries'
import { getFacetFiltersFromSearchParams } from '@/contexts/products/facetFilters'
import { DEFAULT_SORT_ORDERS, getProductOrder, SORT_CRITERIA } from '@/contexts/products/ProductsContext'

export type FavoriteViewResource = 'MODELS' | 'SUPERMODELS'

export type FavoriteViewHierarchy
  = | { familyIds: number[] }
    | { departmentId: number }
    | { subdepartmentId: number }

export type FavoriteViewFamilyPath = 'subdepartment' | 'full-subdepartment' | 'subdepartments'

export interface FavoriteViewRouteContext {
  universeId: number
  departmentId: number
  subdepartmentId?: number
  familyPath: FavoriteViewFamilyPath
}

export interface FavoriteViewFilters {
  product_natures?: number[]
  brands?: number[]
  sports?: number[]
  sports_groups?: number[]
  dmi_segmentations?: string[]
  genders?: number[]
  ecodesign_statuses?: string[]
  design_centers?: number[]
  production_countries?: string[]
  homologation_statuses?: string[]
  homologation_countries?: string[]
  validation_statuses?: string[]
  is_new?: boolean
}

export interface FavoriteViewSearch {
  resource: FavoriteViewResource
  season: string
  hierarchy: FavoriteViewHierarchy
  filters: FavoriteViewFilters
  order: ProductOrder
  routeContext?: FavoriteViewRouteContext
}

interface BuildFavoriteViewSearchOptions {
  season: string | undefined
  universeId?: string
  departmentId: string | undefined
  subdepartmentId: string | undefined
  pathname?: string
  searchParams: URLSearchParams
  countries: CountryItem[]
}

const numericFilterNames = new Set([
  'product_natures',
  'brands',
  'sports',
  'sports_groups',
  'genders',
  'design_centers',
])

const supportedFilterNames: Exclude<keyof FavoriteViewFilters, 'is_new'>[] = [
  'product_natures',
  'brands',
  'sports',
  'sports_groups',
  'dmi_segmentations',
  'genders',
  'ecodesign_statuses',
  'design_centers',
  'production_countries',
  'homologation_statuses',
  'homologation_countries',
  'validation_statuses',
]

function normalizeIds(values: string[]): number[] {
  return [...new Set(values
    .map(Number)
    .filter(value => value > 0))]
    .sort((left, right) => left - right)
}

function getHierarchy(
  searchParams: URLSearchParams,
  departmentId: string | undefined,
  subdepartmentId: string | undefined,
): FavoriteViewHierarchy | null {
  const familyIds = normalizeIds(searchParams.getAll('families'))

  if (familyIds.length > 0)
    return { familyIds }

  if (subdepartmentId && Number.isSafeInteger(Number(subdepartmentId)) && Number(subdepartmentId) > 0)
    return { subdepartmentId: Number(subdepartmentId) }

  if (departmentId && Number.isSafeInteger(Number(departmentId)) && Number(departmentId) > 0)
    return { departmentId: Number(departmentId) }

  return null
}

function getOrder(searchParams: URLSearchParams): ProductOrder {
  const requestedCriteria = searchParams.get('sort')
  const sortCriteria = requestedCriteria && SORT_CRITERIA.includes(requestedCriteria as typeof SORT_CRITERIA[number])
    ? requestedCriteria as typeof SORT_CRITERIA[number]
    : 'segmentation'
  const requestedOrder = searchParams.get('order')
  const sortOrder = requestedOrder === 'asc' || requestedOrder === 'desc'
    ? requestedOrder
    : DEFAULT_SORT_ORDERS[sortCriteria]

  return getProductOrder(sortCriteria, sortOrder) as ProductOrder
}

function getRouteContext(
  universeId: string | undefined,
  departmentId: string | undefined,
  subdepartmentId: string | undefined,
  pathname: string | undefined,
): FavoriteViewRouteContext | undefined {
  const parsedUniverseId = Number(universeId)
  const parsedDepartmentId = Number(departmentId)

  if (!Number.isSafeInteger(parsedUniverseId) || parsedUniverseId <= 0
    || !Number.isSafeInteger(parsedDepartmentId) || parsedDepartmentId <= 0) {
    return undefined
  }

  const parsedSubdepartmentId = Number(subdepartmentId)
  const validSubdepartmentId = Number.isSafeInteger(parsedSubdepartmentId) && parsedSubdepartmentId > 0
    ? parsedSubdepartmentId
    : undefined
  let familyPath: FavoriteViewFamilyPath = 'subdepartment'
  if (pathname?.includes('/full/families'))
    familyPath = 'full-subdepartment'
  else if (pathname?.includes('/subdepartments/families'))
    familyPath = 'subdepartments'

  return {
    universeId: parsedUniverseId,
    departmentId: parsedDepartmentId,
    ...(validSubdepartmentId ? { subdepartmentId: validSubdepartmentId } : {}),
    familyPath,
  }
}

function getSupportedFilters(searchParams: URLSearchParams, countries: CountryItem[]): FavoriteViewFilters {
  const selectedFilters = getFacetFiltersFromSearchParams(searchParams, countries)
  const filters: FavoriteViewFilters = {}

  for (const name of supportedFilterNames) {
    const values = selectedFilters[name]

    if (!values?.length)
      continue

    const normalizedValues = numericFilterNames.has(name)
      ? normalizeIds(values.map(String))
      : [...new Set(values.map(String).map(value => value.trim()).filter(Boolean))]
          .sort((left, right) => left.localeCompare(right))

    if (normalizedValues.length > 0) {
      Object.assign(filters, { [name]: normalizedValues })
    }
  }

  if (searchParams.has('is_new'))
    filters.is_new = true

  return filters
}

export function buildFavoriteViewSearch({
  season,
  universeId,
  departmentId,
  subdepartmentId,
  pathname,
  searchParams,
  countries,
}: BuildFavoriteViewSearchOptions): FavoriteViewSearch | null {
  if (!season || /^(?:SS|AW)\d{2}$/.test(season) === false)
    return null

  const hierarchy = getHierarchy(searchParams, departmentId, subdepartmentId)

  if (!hierarchy)
    return null

  const routeContext = getRouteContext(universeId, departmentId, subdepartmentId, pathname)

  return {
    resource: searchParams.get('viewMode') === 'model' ? 'MODELS' : 'SUPERMODELS',
    season,
    hierarchy,
    filters: getSupportedFilters(searchParams, countries),
    order: getOrder(searchParams),
    ...(routeContext ? { routeContext } : {}),
  }
}

export function getFavoriteViewSearchKey(search: FavoriteViewSearch): string {
  const hierarchy = 'familyIds' in search.hierarchy
    ? { familyIds: normalizeIds(search.hierarchy.familyIds.map(String)) }
    : search.hierarchy
  const filters = Object.fromEntries(
    Object.entries(search.filters)
      .filter(([, value]) => value !== undefined)
      .map(([name, value]) => [name, Array.isArray(value)
        ? [...value].sort((left, right) => String(left).localeCompare(String(right)))
        : value])
      .sort(([left], [right]) => String(left).localeCompare(String(right))),
  )

  return JSON.stringify({
    resource: search.resource,
    season: search.season,
    hierarchy,
    filters,
    order: search.order,
  })
}
